import { getQueryParameterByName } from '@omnia/ui'
import { getFilmById, getShowtimeById } from '@vista-digital/occ-domain/src'
import type { Location, NavigateFunction } from 'react-router-dom-v7'
import { isSignedIn } from '@/common/utils/isSignedIn'
import { getLocalStorage } from '@/common/utils/localStorage/getLocalStorage'
import { setLocalStorage } from '@/common/utils/localStorage/setLocalStorage'
import { navigationService } from '@/content/services/navigation.service'
import type { UrlServiceInterface } from '@/content/services/url.service.interface'
import { ticketingWorkflow } from '@/content/services/workflows/ticketing/ticketing-workflow'
import { isUnallocatedSeating } from '@/content/services/workflows/ticketing/validators/utils/isUnallocatedSeating'

export const handleSeatPickerPageNavigation = (
    currentSubPage: string | undefined,
    navigate: NavigateFunction,
    urlService: UrlServiceInterface,
    location: Location
): boolean => {
    const isFirstPageInAWorkflow = () => {
        // For unallocated seating, the first page is either TicketPicker (if signed in) or TicketingLogin (if not signed in)
        if (isUnallocatedSeating(ticketingWorkflow.State)) {
            if (isSignedIn() && currentSubPage === 'TicketPicker') return true
            if (!isSignedIn() && currentSubPage === 'TicketingLogin') return true
            return false
        }
        return currentSubPage === 'SeatPicker'
    }

    // Skip browser history on the first workflow page: going back to a later step that fails validation causes a redirect loop.
    if (isFirstPageInAWorkflow()) {
        // No meaningful page to return to when entering from the quick navigator.
        if (location.search.includes('fromQuickNavigator=true')) {
            void navigate(navigationService.getPageUrl('Home'))
            return true
        }

        // Cinema and film pages record where the flow was entered from.
        const bookingFlowStartedFromUrl = getLocalStorage<string>('bookingFlowStartedFromUrl')
        if (bookingFlowStartedFromUrl) {
            // Marks a genuine return from the booking flow so scroll position is restored.
            setLocalStorage('restoreScrollOnBookingFlowReturn', true)
            void navigate(bookingFlowStartedFromUrl)
            return true
        }

        const showtimeId = getQueryParameterByName('showtimeId', location.search)
        if (showtimeId) {
            const showtime = getShowtimeById(showtimeId)
            const filmId = showtime?.filmId
            if (filmId) {
                const film = getFilmById(filmId)
                if (film) {
                    void navigate(urlService.getFilmDetailsUrl(film))
                    return true
                }
            }
        }
    }
    return false
}
